import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { debounce } from "lodash";

const HTTPS_PREFIX = "https://";
const PLACEHOLDER = "www.vendorwebsite.com";
const MAX_URL_LENGTH = 2048;

/** Only allow ASCII printable + space; reject control chars and non-ASCII (homograph/encoding attacks). */
function isOnlySafeAscii(str) {
  if (typeof str !== "string") return false;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x20 || code > 0x7e) return false; // control chars and non-ASCII
  }
  return true;
}

/**
 * Strip https:// or http:// from URL for display in input (user edits domain only).
 */
export function getDisplayValue(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (trimmed.toLowerCase().startsWith("https://"))
    return trimmed.slice(8).trim();
  if (trimmed.toLowerCase().startsWith("http://")) return trimmed.slice(7).trim();
  return trimmed;
}

/**
 * Normalize user input to full HTTPS URL for storage. Returns null if invalid.
 * Rejects: non-HTTPS, non-ASCII/homograph, malformed URLs, excess length.
 */
export function normalizeToHttpsUrl(raw) {
  if (raw === undefined || raw === null) return "";
  let trimmed = String(raw).trim();
  if (!trimmed) return "";
  if (trimmed.length > MAX_URL_LENGTH) return null;
  if (!isOnlySafeAscii(trimmed)) return null;

  const lower = trimmed.toLowerCase();
  if (lower.startsWith("http://")) return null;
  // Trim trailing dots so "facebook.com." parses as "facebook.com" (URL API strips host trailing dot; avoid throwing)
  const trimmedTrailingDots = trimmed.replace(/\.+$/, "");
  const toParse = lower.startsWith("https://")
    ? trimmedTrailingDots
    : HTTPS_PREFIX + trimmedTrailingDots;

  try {
    const url = new URL(toParse);
    if (url.protocol !== "https:") return null;
    const host = url.hostname || "";
    if (!host || host.length > 253) return null;
    // Host format: letters, digits, hyphen, dot
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9-.]*[a-zA-Z0-9])?$/.test(host) && host !== "localhost") return null;
    // Reject single-label hosts (e.g. "h", "x") – require at least one dot for a real domain (allow localhost)
    if (host !== "localhost" && !host.includes(".")) return null;
    // Reject IPv4 that the URL parser "compressed" from a short input (e.g. "1" -> 0.0.0.1, "234" -> 0.0.0.234)
    const isIpv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
    const inputDomain = lower.startsWith("https://") ? trimmedTrailingDots.slice(8).trim() : lower.startsWith("http://") ? trimmedTrailingDots.slice(7).trim() : trimmedTrailingDots;
    const dotCount = (inputDomain.match(/\./g) || []).length;
    if (isIpv4 && dotCount < 3) return null;
    // Return URL without trailing slash on origin (avoid adding slash when user typed "facebook.com")
    const path = url.pathname === "/" ? "" : url.pathname;
    const q = url.search || "";
    const h = url.hash || "";
    return url.origin + path + q + h;
  } catch {
    return null;
  }
}

/**
 * Validate URL: must be HTTPS, ASCII-only, well-formed. Returns { valid: boolean, message?: string }.
 */
export function validateHttpsUrl(value) {
  if (value === undefined || value === null || String(value).trim() === "") return { valid: true };
  const str = String(value).trim();
  if (str.length > MAX_URL_LENGTH) return { valid: false, message: "URL is too long." };
  if (!isOnlySafeAscii(str)) return { valid: false, message: "URL contains invalid characters. Use only standard letters and numbers." };
  const lower = str.toLowerCase();
  if (lower.startsWith("http://")) return { valid: false, message: "Only HTTPS URLs are accepted for security compliance." };
  const normalized = normalizeToHttpsUrl(value);
  if (normalized === null) return { valid: false, message: "Enter a valid HTTPS URL with a proper domain (e.g. www.example.com or facebook.com)." };
  if (normalized === "") return { valid: false, message: "Please enter a valid HTTPS URL." };
  return { valid: true };
}

function HttpsUrlInputInner({
  name,
  label = "Vendor URL",
  placeholder = PLACEHOLDER,
  disabled = false,
  value,
  onChange,
  onBlur,
  errorMessage,
  className = "",
  inputClassName = "",
}) {
  const [displayValue, setDisplayValue] = useState(() => getDisplayValue(value));
  const [validationStatus, setValidationStatus] = useState(null); // null | { valid: boolean, message?: string }
  const [touched, setTouched] = useState(false);

  const runValidation = useCallback(
    debounce((val) => {
      const result = validateHttpsUrl(val);
      setValidationStatus(result);
      return result;
    }, 500),
    []
  );

  useEffect(() => {
    setDisplayValue(getDisplayValue(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    const domainOnly = getDisplayValue(raw);
    setDisplayValue(domainOnly);
    const normalized = normalizeToHttpsUrl(raw);
    // Only store valid HTTPS URLs; never store malformed or http to avoid injection/invalid data
    if (normalized !== null && normalized !== "") {
      onChange(normalized);
    } else if (raw.trim() === "") {
      // User cleared the input
      onChange("");
    }
    // When invalid but non-empty (e.g. "facebookcom" after removing dot), do not clear form value – keep previous so field doesn't disappear
    runValidation(raw);
  };

  const handleBlur = (e) => {
    setTouched(true);
    const raw = e.target.value;
    const result = validateHttpsUrl(raw);
    setValidationStatus(result);
    if (result.valid && raw.trim()) {
      const normalized = normalizeToHttpsUrl(raw);
      if (normalized) onChange(normalized);
    }
    onBlur?.(e);
  };

  const showError = (touched && validationStatus && !validationStatus.valid) || errorMessage;
  const errMsg = errorMessage || (validationStatus && !validationStatus.valid ? validationStatus.message : null);
  const isValid = touched && validationStatus?.valid && displayValue.trim() !== "";

  // Always show displayValue in the input so that partial/invalid edits (e.g. "facebookcom" after removing the dot) are not overwritten by the stored value on re-render
  const inputValue = displayValue.trim();

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-900 mb-1">
          {label}
        </label>
      )}
      <div
        className={`flex rounded-md outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 ${disabled ? "bg-gray-100" : ""}`}
        style={{ minHeight: "38px" }}
      >
        <span
          aria-hidden="true"
          className="flex select-none items-center pl-3 text-gray-500 sm:text-sm shrink-0"
          style={{ color: "#6b7280" }}
        >
          {HTTPS_PREFIX}
        </span>
        <input
          type="text"
          name={name}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={label}
          aria-invalid={!!showError}
          aria-describedby={showError ? `${name}-error` : undefined}
          className={`block flex-1 border-0 bg-transparent py-1.5 pl-1 pr-10 text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6 ${inputClassName}`}
          style={{ minWidth: 0 }}
        />
        <div className="flex items-center pr-3 pointer-events-none">
          {isValid && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          )}
          {showError && !isValid && displayValue.trim() !== "" && (
            <XCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          )}
        </div>
      </div>
      {showError && errMsg && (
        <p id={`${name}-error`} className="mt-1 text-sm text-red-600">
          {errMsg}
        </p>
      )}
    </div>
  );
}

/**
 * HttpsUrlInput for use inside react-hook-form (FormProvider).
 * Uses Controller; stores full "https://..." value in form state.
 */
export function HttpsUrlInput({
  name,
  label = "Vendor URL",
  placeholder = PLACEHOLDER,
  disabled = false,
  className = "",
}) {
  const { control, formState: { errors } } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <HttpsUrlInputInner
          name={name}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          errorMessage={errors[name]?.message}
          className={className}
        />
      )}
    />
  );
}

/**
 * Standalone HttpsUrlInput (no FormProvider). Controlled: value + onChange.
 * onChange receives full URL with https:// for storage.
 */
export function HttpsUrlInputStandalone({
  name,
  label = "Vendor URL",
  placeholder = PLACEHOLDER,
  disabled = false,
  value,
  onChange,
  onBlur,
  errorMessage,
  className = "",
}) {
  return (
    <HttpsUrlInputInner
      name={name}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      errorMessage={errorMessage}
      className={className}
    />
  );
}

export default HttpsUrlInput;
